/**
 * Pacing Dashboard Grid
 * =====================
 * Drag-and-drop, resizable grid for pacing KPI cards.
 * Auto-saves layout changes per user/page.
 * Uses consistent card styling with MarketingReportWidget.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { toast } from 'sonner';
import { 
  Loader2, 
  GripVertical, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Save,
  Check,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

// Status color mapping
const STATUS_COLORS = {
  green: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-500'
  },
  yellow: {
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    text: 'text-yellow-600 dark:text-yellow-400',
    badge: 'bg-yellow-500'
  },
  red: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-500'
  }
};

// Metric display configuration
const METRIC_CONFIG = {
  leads: { label: 'Leads', format: 'number', icon: '👥' },
  appointments: { label: 'Appointments', format: 'number', icon: '📅' },
  demos: { label: 'Demos', format: 'number', icon: '🎯' },
  sales: { label: 'Sales', format: 'number', icon: '✅' },
  revenue: { label: 'Revenue', format: 'currency', icon: '💰' },
  spend: { label: 'Ad Spend', format: 'currency', icon: '📊', invertColors: true },
  roas: { label: 'ROAS', format: 'multiplier', icon: '📈', suffix: 'x' },
  cpa: { label: 'CPA', format: 'currency', icon: '💵', invertColors: true },
  cpl: { label: 'CPL', format: 'currency', icon: '💸', invertColors: true }
};

// Default layout for pacing cards
const DEFAULT_LAYOUT = [
  { i: 'leads', x: 0, y: 0, w: 4, h: 2 },
  { i: 'appointments', x: 4, y: 0, w: 4, h: 2 },
  { i: 'sales', x: 8, y: 0, w: 4, h: 2 },
  { i: 'revenue', x: 0, y: 2, w: 4, h: 2 },
  { i: 'spend', x: 4, y: 2, w: 4, h: 2 },
  { i: 'roas', x: 8, y: 2, w: 4, h: 2 }
];

const PacingCard = ({ metric, data, config }) => {
  const colors = STATUS_COLORS[data?.status] || STATUS_COLORS.yellow;
  
  const formatValue = (value, format) => {
    if (value === null || value === undefined) return '-';
    
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      case 'multiplier':
        return value.toFixed(2);
      case 'percent':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString();
    }
  };
  
  const getTrendIcon = () => {
    if (!data?.trend) return null;
    if (data.trend === 'up') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (data.trend === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };
  
  return (
    <Card 
      className={`h-full ${colors.bg} ${colors.border} border transition-all hover:shadow-md`}
      data-testid={`pacing-card-${metric}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <div className="widget-drag-handle cursor-move p-1 hover:bg-muted/50 rounded">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <span>{config?.icon}</span>
            <span>{config?.label}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {getTrendIcon()}
            <div className={`w-2 h-2 rounded-full ${colors.badge}`} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Actual Value */}
        <div>
          <p className={`text-2xl font-bold ${colors.text}`}>
            {formatValue(data?.actual, config?.format)}
            {config?.suffix && <span className="text-lg ml-1">{config?.suffix}</span>}
          </p>
          <p className="text-xs text-muted-foreground">
            Actual {data?.source && `(${data.source})`}
          </p>
        </div>
        
        {/* Target & Projection */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-muted-foreground text-xs">Target</p>
            <p className="font-medium">
              {formatValue(data?.target, config?.format)}
              {config?.suffix && config.format === 'multiplier' && config?.suffix}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Projected</p>
            <p className={`font-medium ${colors.text}`}>
              {formatValue(data?.pace_projection, config?.format)}
              {config?.suffix && config.format === 'multiplier' && config?.suffix}
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Progress to Target</span>
            <span>{data?.percent_of_target?.toFixed(0) || 0}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full ${colors.badge} transition-all duration-500`}
              style={{ width: `${Math.min(100, data?.percent_of_target || 0)}%` }}
            />
          </div>
        </div>
        
        {/* Gap to Target */}
        {data?.gap_to_target !== undefined && data?.gap_to_target !== 0 && (
          <p className={`text-xs ${data.gap_to_target > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {data.gap_to_target > 0 ? '+' : ''}
            {formatValue(data.gap_to_target, config?.format)} vs target
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const PacingDashboardGrid = ({ 
  pacingData, 
  loading,
  onRefresh,
  visibleMetrics = ['leads', 'appointments', 'sales', 'revenue', 'spend', 'roas']
}) => {
  const [layout, setLayout] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  
  // Define fetchLayout BEFORE useEffect
  const fetchLayout = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/marketing/dashboard/layout?page=on_track_pacing`);
      
      if (response.data?.widgets?.length > 0) {
        // Convert stored widgets to react-grid-layout format
        const savedLayout = response.data.widgets.map(w => ({
          i: w.id,
          x: w.x,
          y: w.y,
          w: w.w,
          h: w.h
        }));
        setLayout(savedLayout);
      } else {
        // Use default layout filtered by visible metrics
        const defaultFiltered = DEFAULT_LAYOUT.filter(item => 
          visibleMetrics.includes(item.i)
        );
        setLayout(defaultFiltered);
      }
    } catch (error) {
      console.error('Error fetching pacing layout:', error);
      // Use default layout on error
      const defaultFiltered = DEFAULT_LAYOUT.filter(item => 
        visibleMetrics.includes(item.i)
      );
      setLayout(defaultFiltered);
    }
  }, [visibleMetrics]);
  
  // Calculate container width for responsive grid
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  const saveLayout = useCallback(async (newLayout) => {
    setSaving(true);
    setSaveStatus('saving');
    
    try {
      const widgets = newLayout.map(item => ({
        id: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      }));
      
      await axios.post(`${API}/api/marketing/dashboard/layout?page=on_track_pacing`, {
        widgets
      });
      
      setSaveStatus('saved');
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving pacing layout:', error);
      setSaveStatus('error');
      toast.error('Failed to save layout');
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } finally {
      setSaving(false);
    }
  }, []);
  
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
  }, []);
  
  const handleDragStop = useCallback((newLayout) => {
    setLayout(newLayout);
    saveLayout(newLayout);
  }, [saveLayout]);
  
  const handleResizeStop = useCallback((newLayout) => {
    setLayout(newLayout);
    saveLayout(newLayout);
  }, [saveLayout]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  // Initialize layout with defaults if empty
  const currentLayout = layout.length > 0 ? layout : DEFAULT_LAYOUT.filter(item => 
    visibleMetrics.includes(item.i)
  );
  
  // Filter layout to only include visible metrics that have data
  const activeLayout = currentLayout.filter(item => 
    visibleMetrics.includes(item.i) && pacingData?.metrics?.[item.i]
  );
  
  return (
    <div ref={containerRef} className="w-full relative">
      {/* Auto-save status indicator */}
      {saveStatus && (
        <div 
          className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg transition-all duration-300 ${
            saveStatus === 'saving' 
              ? 'bg-blue-500 text-white' 
              : saveStatus === 'saved' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
          }`}
          data-testid="pacing-save-indicator"
        >
          {saveStatus === 'saving' && (
            <>
              <Save className="h-4 w-4 animate-pulse" />
              <span className="text-sm font-medium">Saving layout...</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <Check className="h-4 w-4" />
              <span className="text-sm font-medium">Layout saved</span>
            </>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm font-medium">Failed to save</span>
          )}
        </div>
      )}
      
      {activeLayout.length > 0 ? (
        <GridLayout
          className="layout"
          layout={activeLayout}
          cols={12}
          rowHeight={80}
          width={containerWidth}
          onLayoutChange={handleLayoutChange}
          onDragStop={handleDragStop}
          onResizeStop={handleResizeStop}
          draggableHandle=".widget-drag-handle"
          isResizable={true}
          isDraggable={true}
          compactType="vertical"
          preventCollision={false}
          margin={[16, 16]}
        >
          {activeLayout.map(item => (
            <div key={item.i}>
              <PacingCard
                metric={item.i}
                data={pacingData?.metrics?.[item.i]}
                config={METRIC_CONFIG[item.i]}
              />
            </div>
          ))}
        </GridLayout>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <p>No pacing data available</p>
          {onRefresh && (
            <button 
              onClick={onRefresh}
              className="mt-2 text-sm text-primary hover:underline flex items-center gap-1 mx-auto"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh Data
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PacingDashboardGrid;
